import {
  Bot,
  CalendarCheck,
  CalendarDays,
  Crown,
  Globe,
  Percent,
  ShieldCheck,
  Users,
  UserCog,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/*
 * CONFIGURAÇÃO DA MARCA DA LANDING (ponto único de troca: paleta, rotas, textos, ícones e imagens).
 * `kit.tsx` (primitivas) e `landing.css` (lê só as variáveis --lp-*) vêm do molde do PetFlow/StayControl.
 * Os mockups do domínio ficam em `parts.tsx` e são registrados em VISUALS (`sections.tsx`).
 * Regra de ouro: só prometer o que o app faz de verdade (conferir em docs/ e no CLAUDE.md).
 *
 * TEMA ESCURO (palette.theme = 'dark'): a página inteira é escura e continua 100% palette-driven.
 *  - `theme` vira `data-lp-theme` no elemento raiz; `landing.css` tem UM bloco `[data-lp-theme='dark']`
 *    (sombras, brilho no hover, chips de status, véu do hero) que só lê variáveis --lp-*.
 *  - Para portar (ex.: GymBoss): troque as cores da paleta (bg/bgAlt/surface/ink/muted/line + accent/cta/gold),
 *    `focus` (anel de foco), `glow` (RGB do brilho no hover) e `shadow` ('0 0 0' no escuro). Contraste AA:
 *    texto claro sobre escuro; o destaque só em título, ícone e botão; texto ESCURO (ctaInk) sobre o destaque.
 *  - O bloco de contraste do meio ("Como começar") usa bandFrom/bandTo/bandInk (aqui dourado suave com texto
 *    escuro); a faixa final usa finFrom/finTo/finInk (aqui preto com foto). Em tema claro, `theme: 'light'`
 *    (padrão) e nada muda.
 */

export interface Img {
  src: string;
  width: number;
  height: number;
  alt: string;
}

export interface Block {
  id: string;
  title: string;
  text: string;
  bullets?: readonly string[];
  image: Img;
  /** lado em que a foto fica no desktop */
  side: 'left' | 'right';
  /** mockup de tela sobreposto à foto (chave de VISUALS em sections.tsx) */
  visual?: 'agenda' | 'caixa' | 'fidelidade';
  /** classe de proporção da foto, ex.: 'aspect-[4/5]' */
  ratio?: string;
}

export const BRAND = {
  name: { strong: 'Barbeiro', light: 'Pro' },
  /** logotipo (SVG com wordmark em traço claro, próprio para fundo escuro) */
  logo: { src: '/brand/logo-light.svg', width: 340, height: 72, alt: 'BarbeiroPro' },
  homeLabel: 'BarbeiroPro, início',
  meta: {
    title: 'BarbeiroPro | Agenda, caixa e site para barbearias',
    description:
      'Agenda por profissional sem horário duplicado, site com agendamento online, caixa com fechamento, comissões, fidelidade e assistente de IA para a sua barbearia.',
  },

  /** vira variáveis CSS --lp-* no elemento raiz */
  palette: {
    theme: 'dark' as 'light' | 'dark',
    /** preto da página */
    bg: '#0B0B0C',
    /** segundo tom: seções alternadas */
    bgAlt: '#101012',
    /** grafite dos cartões, da faixa e do cabeçalho */
    surface: '#151517',
    header: '#0B0B0C',
    /** branco quente (AA sobre preto e sobre grafite) */
    ink: '#F5F4F0',
    /** cinza claro: ~9:1 sobre #0B0B0C e ~8:1 sobre o grafite */
    muted: '#B8B8BE',
    line: '#2A2A2E',
    /** dourado da marca: destaque, ícones, pontos e bordas */
    accent: '#F6C85F',
    accentInk: '#F6C85F',
    accentText: '#F6C85F',
    /** fundo dos chips de ícone (dourado a ~12% sobre o grafite) */
    accentSoft: '#2A2415',
    /** destaque DENTRO do bloco dourado do meio: tinta escura (AA sobre o dourado) */
    accentLight: '#17120A',
    /** botão primário dourado com texto escuro (~11:1) */
    cta: '#F6C85F',
    ctaHover: '#FFD878',
    ctaInk: '#17120A',
    gold: '#F6C85F',
    goldHover: '#FFD878',
    goldInk: '#17120A',
    /** bloco do meio ("Como começar"): dourado suave com texto escuro */
    bandFrom: '#F6C85F',
    bandTo: '#E4B246',
    bandInk: '#17120A',
    /** faixa final: preto/grafite com foto e texto branco */
    finFrom: '#0B0B0C',
    finTo: '#1B1B1E',
    finInk: '#FFFFFF',
    /** anel de foco visível sobre fundo escuro */
    focus: '#F6C85F',
    /** RGB do brilho dourado no hover dos cartões */
    glow: '246 200 95',
    shadow: '0 0 0',
  },

  /* a landing é a raiz do site */
  routes: { home: '/', signup: '/cadastrar', login: '/entrar' },
  cta: { signup: 'Criar conta da barbearia', login: 'Entrar' },

  nav: [
    { href: '#modulos', label: 'O que tem' },
    { href: '#comecar', label: 'Como começar' },
    { href: '#seguranca', label: 'Segurança' },
    { href: '#perguntas', label: 'Perguntas' },
  ],

  hero: {
    variant: 'split' as 'split' | 'immersive',
    before: 'Mais que um corte,',
    highlight: 'uma experiência.',
    after: ' E uma agenda cheia.',
    subtitle:
      'Agenda por profissional, site com agendamento online, caixa e comissões num sistema só, para a sua barbearia atender bem e fechar o dia sem planilha.',
    image: {
      src: '/landing/barbeiropro-hero.webp',
      width: 1376,
      height: 768,
      alt: 'Barbeiro de camisa preta e luvas faz a barba de um cliente reclinado na cadeira de couro, numa barbearia de luz âmbar com poste de barbeiro ao fundo.',
    } satisfies Img,
    /** os sujeitos ficam na metade direita: ancora o recorte à direita */
    focus: '100% 50%',
    /* 3 linhas fixas; a mais longa ("E uma agenda cheia.") ~8,5em: 5.4vw mantém ~46% da largura (termina antes dos sujeitos, a ~54%) */
    titleMax: '6rem',
    titleVw: 5.4,
    threeLines: true,
    photoWidth: '66%',
  },

  /** faixa de 4 diferenciais (só funcionalidades reais) */
  strip: [
    { icon: CalendarCheck, a: 'Agenda sem', b: 'horário duplicado' },
    { icon: Globe, a: 'Site com', b: 'agendamento online' },
    { icon: Wallet, a: 'Caixa e comissões', b: 'por profissional' },
    { icon: Crown, a: 'Fidelidade, planos', b: 'e cupons' },
  ] as { icon: LucideIcon; a: string; b: string }[],

  /** mockup do painel (rótulo "exemplo ilustrativo"); os campos são os que o painel do dia realmente mostra */
  panel: {
    id: 'painel',
    title: 'Seu dia em números, no painel.',
    intro: 'O painel do dia reúne estes indicadores. Os valores abaixo são fictícios.',
    label: 'exemplo ilustrativo',
    items: [
      { value: '12', label: 'Atendimentos hoje', hint: '3 ainda por atender' },
      { value: 'R$ 1.480', label: 'Faturado hoje', hint: 'recebido no caixa' },
      { value: '68%', label: 'Ocupação', hint: 'da agenda de hoje' },
      { value: 'R$ 123', label: 'Ticket médio', hint: 'por atendimento' },
    ],
  },

  modules: {
    id: 'modulos',
    title: 'A rotina da barbearia, da cadeira ao caixa.',
    intro:
      'Agenda, clientes, financeiro e site público trabalham sobre os mesmos dados. O que o cliente marca pelo site aparece na agenda, e o que é pago no balcão vira caixa e comissão.',
    items: [
      {
        icon: CalendarDays,
        title: 'Agenda por profissional',
        text: 'Cada barbeiro tem a própria grade. Dois atendimentos ativos no mesmo horário não passam, nem quando o pedido vem do site. Lista de espera por data, serviço e profissional.',
      },
      {
        icon: Users,
        title: 'Clientes',
        text: 'Cadastro com telefone e e-mail. Campanhas por e-mail saem só para quem deu o consentimento, com a data registrada.',
      },
      {
        icon: Crown,
        title: 'Fidelidade e planos',
        text: 'Pontos por real pago ou por visita, planos recorrentes e pacotes pré-pagos. Plano e pacote só ativam depois do recebimento no caixa.',
      },
      {
        icon: Percent,
        title: 'Cupons',
        text: 'Desconto com vigência, valor mínimo, serviços e limite de uso total e por cliente.',
      },
      {
        icon: Wallet,
        title: 'Caixa e fechamento',
        text: 'Abertura com valor inicial, recebimento em até cinco meios de pagamento e fechamento que compara o dinheiro esperado com o contado.',
      },
      {
        icon: UserCog,
        title: 'Comissões',
        text: 'Regra por profissional, serviço ou geral, em percentual ou valor fixo. A comissão sai do recebimento e é estornada junto com ele.',
      },
      {
        icon: Globe,
        title: 'Site com agendamento',
        text: 'Página pública da barbearia com serviços, equipe, galeria e horários livres, editável por blocos. Só aparece o que você publicou.',
      },
      {
        icon: Bot,
        title: 'Assistente de IA',
        text: 'Resumo do dia, horários ociosos, clientes inativos e perguntas sobre os seus indicadores. Ele propõe, e quem executa é você.',
      },
    ] as { icon: LucideIcon; title: string; text: string }[],
  },

  blocks: [
    {
      id: 'agenda',
      title: 'Uma agenda por profissional, sem horário duplicado.',
      text: 'A disponibilidade respeita o expediente da unidade e de cada barbeiro, feriados, bloqueios e o intervalo entre atendimentos. O banco de dados é a barreira final contra dois clientes no mesmo horário.',
      bullets: [
        'O barbeiro vê a própria agenda, e o dono vê a casa inteira.',
        'Todo agendamento e mudança de status guarda quem fez e quando.',
        'Cliente cancela pelo link, dentro do prazo da sua política.',
      ],
      image: {
        src: '/landing/barbeiropro-cadeira.webp',
        width: 1200,
        height: 805,
        alt: 'Barbearia com cadeiras de couro preto diante de espelhos emoldurados e iluminados por luz âmbar, bancada de madeira escura e lâmpadas penduradas.',
      },
      side: 'left',
      visual: 'agenda',
    },
    {
      id: 'caixa',
      title: 'Recebeu no balcão, o caixa e a comissão já sabem.',
      text: 'O recebimento exige caixa aberto, pode ser dividido entre Pix, cartão e dinheiro e gera o movimento do caixa. A comissão do profissional é calculada na hora, com a regra gravada junto.',
      bullets: [
        'Estorno desfaz o recebimento, a comissão, os pontos e o cupom.',
        'Sangria, suprimento e ajuste ficam registrados com motivo.',
        'Reenviar o mesmo pagamento nunca cobra duas vezes.',
      ],
      image: {
        src: '/landing/barbeiropro-recepcao.webp',
        width: 1200,
        height: 805,
        alt: 'Recepcionista de camisa preta sorri e mostra um tablet a um cliente sorridente, no balcão de madeira de uma barbearia com poste de barbeiro ao fundo.',
      },
      side: 'right',
      visual: 'caixa',
    },
    {
      id: 'fidelidade',
      title: 'Cliente que sai satisfeito volta, e o sistema ajuda a trazê-lo.',
      text: 'Crie um programa de pontos ou de visitas, venda planos e pacotes e emita cupons. O crédito de pontos acontece uma única vez por pagamento, e o cliente só recebe campanhas se autorizou.',
      bullets: [
        'Pontos creditados só com pagamento confirmado.',
        'Uso de pacote debita o saldo no próprio atendimento.',
        'Campanhas por e-mail apenas para quem consentiu.',
      ],
      image: {
        src: '/landing/barbeiropro-espelho.webp',
        width: 1200,
        height: 805,
        alt: 'Cliente sorridente de cabelo degradê e barba aparada confere o corte no espelho de mão, enquanto o barbeiro de avental preto sorri atrás dele.',
      },
      side: 'left',
      visual: 'fidelidade',
    },
    {
      id: 'site',
      title: 'Seu site próprio, com horário livre para o cliente marcar.',
      text: 'A barbearia ganha uma página com serviços, equipe, galeria, localização e agendamento online, montada por blocos e publicada quando você quiser. O cliente escolhe o profissional e o horário disponível, sem mandar mensagem.',
      bullets: [
        'Rascunho e versões publicadas, com opção de restaurar.',
        'Só texto e links validados: nada de código arbitrário no site.',
        'Avaliações só aparecem depois de autorizadas.',
      ],
      image: {
        src: '/landing/barbeiropro-navalha.webp',
        width: 1200,
        height: 805,
        alt: 'Navalha, tesoura e pincel de barba sobre uma bancada de pedra escura, ao lado de uma tigela de latão, sob luz âmbar.',
      },
      side: 'right',
    },
  ] as Block[],

  steps: {
    id: 'comecar',
    title: 'Da conta criada à agenda aberta.',
    intro: 'Ao entrar, a tela de primeiros passos leva você por estas etapas, na ordem.',
    items: [
      { title: 'Unidade e endereço', text: 'Onde os clientes vão encontrar você.' },
      { title: 'Horário de funcionamento', text: 'A agenda só oferece horários dentro do expediente.' },
      { title: 'Serviços e preços', text: 'O que o cliente pode agendar, com duração e valor.' },
      { title: 'Equipe', text: 'Quem atende. Cada profissional tem a própria agenda.' },
      { title: 'Publicar o site', text: 'Página com seus serviços, equipe e agendamento online.' },
    ],
  },

  security: {
    id: 'seguranca',
    title: 'Dados de clientes e dinheiro pedem controle que dá para conferir.',
    intro:
      'Estes são os controles que o sistema aplica hoje. Cada um está no código, e as operações críticas ficam na auditoria.',
    image: {
      src: '/landing/barbeiropro-balcao.webp',
      width: 1200,
      height: 805,
      alt: 'Balcão escuro de uma barbearia com gaveta de caixa fechada, maquininha de cartão e tablet, sob a luz âmbar de uma luminária.',
    } satisfies Img,
    controls: [
      { title: 'Sessão protegida', text: 'O acesso fica em cookie httpOnly, e toda operação que altera dados exige proteção contra CSRF.' },
      { title: 'Uma barbearia não vê a outra', text: 'A barbearia é resolvida no servidor a cada requisição. O navegador nunca informa de qual barbearia são os dados.' },
      { title: 'Senhas protegidas', text: 'Senhas guardadas com argon2id, e login e recuperação respondem igual para conta existente ou não.' },
      { title: 'Permissões por papel', text: 'Proprietário, gerente, recepção e barbeiro, com permissões editáveis e papéis personalizados.' },
      { title: 'Auditoria', text: 'Financeiro, permissões, status e reagendamento da agenda, publicação do site e configurações geram registro de quem fez.' },
      { title: 'Dinheiro sem arredondamento', text: 'Valores em centavos inteiros e percentuais em pontos-base: nenhum cálculo financeiro usa número decimal aproximado.' },
      { title: 'Site sem código arbitrário', text: 'O conteúdo editável é texto puro em blocos tipados, e os endereços são validados antes de publicar.' },
    ],
    caveat:
      'A conformidade com a LGPD também depende da operação da barbearia: base legal, contratos e política de privacidade. O sistema entrega os controles, e a validação jurídica continua sendo da barbearia.',
  },

  faq: {
    id: 'perguntas',
    title: 'Perguntas de quem decide.',
    items: [
      {
        q: 'O cliente marca horário sozinho pelo site?',
        a: 'Sim. A barbearia ganha uma página pública com agendamento online: o cliente escolhe serviço, profissional e um horário livre. O sistema recalcula a disponibilidade no momento de gravar, então o mesmo horário não é vendido duas vezes.',
      },
      {
        q: 'Cada barbeiro tem a própria agenda?',
        a: 'Sim. Cada profissional tem grade, preço e duração próprios por serviço. O barbeiro vê e opera os próprios atendimentos, e o dono ou o gerente vê a casa inteira.',
      },
      {
        q: 'Como funcionam as comissões?',
        a: 'Você define a regra por profissional, por serviço ou geral, em percentual ou valor fixo. Ao receber, o sistema grava a comissão com a regra usada naquele momento. Se o pagamento for estornado, a comissão também é.',
      },
      {
        q: 'O sistema cobra do meu cliente ou emite nota?',
        a: 'Não. O caixa registra o que foi recebido em Pix, cartão ou dinheiro, mas não há gateway de cobrança nem emissão de nota fiscal. Planos e pacotes só ativam quando o recebimento é lançado.',
      },
      {
        q: 'A IA agenda ou envia mensagens sozinha?',
        a: 'Não. O assistente consulta os dados autorizados da sua barbearia e propõe ações, como uma campanha. Quem executa é você, pelas telas normais. Ele nunca inventa disponibilidade.',
      },
      {
        q: 'Usar o sistema deixa a barbearia em dia com a LGPD?',
        a: 'Ele ajuda com controles técnicos, consentimento de campanhas e rastreabilidade. Base legal, contratos e política de privacidade são responsabilidades da barbearia, com validação jurídica.',
      },
      {
        q: 'Quanto custa?',
        a: 'Esta página não traz valores. Fale conosco para tratar das condições comerciais.',
      },
    ],
  },

  finalCta: {
    title: 'Abra a agenda de amanhã já organizada.',
    image: '/landing/barbeiropro-cta.webp',
  },

  footer: {
    note: 'Esta página não coleta dados pessoais. Os dados dos clientes são tratados pela barbearia, que é a controladora, e pelo BarbeiroPro, como operador, conforme o contrato entre as partes.',
    links: [
      { href: '#modulos', label: 'O que tem' },
      { href: '#seguranca', label: 'Segurança' },
    ],
    /** páginas legais (rotas internas) */
    legal: [
      { to: '/termos', label: 'Termos de Uso' },
      { to: '/privacidade', label: 'Política de Privacidade' },
    ],
  },
} as const;

export type BrandConfig = typeof BRAND;

/** Converte a paleta em variáveis CSS --lp-* (aplicadas no elemento raiz da landing). */
export function paletteVars(p: BrandConfig['palette']): Record<string, string> {
  const dark = p.theme === 'dark';
  return {
    '--lp-bg': p.bg,
    '--lp-bg-alt': p.bgAlt,
    '--lp-surface': p.surface,
    '--lp-header': p.header,
    '--lp-ink': p.ink,
    '--lp-muted': p.muted,
    '--lp-line': p.line,
    '--lp-accent': p.accent,
    '--lp-accent-ink': p.accentInk,
    '--lp-accent-soft': p.accentSoft,
    '--lp-accent-light': p.accentLight,
    '--lp-accent-text': p.accentText,
    /* claro: anel de foco na cor do texto; escuro: na cor do destaque */
    '--lp-focus': dark ? p.focus : p.ink,
    '--lp-cta': p.cta,
    '--lp-cta-hover': p.ctaHover,
    '--lp-cta-ink': p.ctaInk,
    '--lp-gold': p.gold,
    '--lp-gold-hover': p.goldHover,
    '--lp-gold-ink': p.goldInk,
    '--lp-band-from': p.bandFrom,
    '--lp-band-to': p.bandTo,
    '--lp-band-ink': p.bandInk,
    '--lp-fin-from': p.finFrom,
    '--lp-fin-to': p.finTo,
    '--lp-fin-ink': p.finInk,
    '--lp-glow': p.glow,
    '--lp-shadow': p.shadow,
  };
}
